import { MutableRefObject, useEffect, useMemo, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  CopyPhoneButton,
  DatePicker,
  GenderRadioGroup,
  InputLabel,
  Select,
  TextInput,
  TimeRangeSlider,
} from "components";
import { convertToTimeString } from "components/time";
import { getRequiredFieldStyles } from "pages/clients/view/components/required-field-styles";
import SectionTitle from "pages/clients/view/components/section-title";

import { BankCreditOptionsResponse, BankCreditRequestForm, BankCreditResponse } from "types";
import { AppUtils } from "utils";

interface CreditRequestPersonalInfoProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  updateCredit: (item: Partial<BankCreditRequestForm>) => Promise<void>;
  revalidateEmploymentDatesRef?: MutableRefObject<
    ((newBirthDate?: Date | undefined | null, newVisaFrom?: Date | undefined | null) => void) | null
  >;
}

export const BankCreditRequestPersonalInfo = ({
  credit,
  form,
  options,
  visibleFields,
  readOnly = false,
  updateCredit,
  revalidateEmploymentDatesRef,
}: CreditRequestPersonalInfoProps) => {
  const { t } = useTranslation();
  const availabilitySliderMaxWidth = 420;
  const [visaInputKey, setVisaInputKey] = useState(0);

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options["nationality"], t),
    [options, t],
  );

  const creditLanguageOptions = useMemo(
    () =>
      options["language"]
        .filter((e) => e === "DE" || e === "FR" || e === "IT")
        .map((el) => ({
          value: el,
          label: t(`bankCredit.creditLanguage.options.${el}`),
        })),
    [options, t],
  );

  useEffect(() => {
    // Only save calculated default if backend doesn't have a value yet
    if (!credit.creditLanguage && !readOnly && form.values.creditLanguage) {
      updateCredit({
        creditLanguage: form.values.creditLanguage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nationality = form.getValues().nationality;
  const canton = form.getValues().canton;

  useEffect(() => {
    // Update creditLanguage whenever nationality or canton changes
    if (!readOnly && (nationality || canton)) {
      const newLanguage = AppUtils.getCreditLanguage(nationality, canton);

      if (newLanguage !== form.values.creditLanguage) {
        form.setFieldValue("creditLanguage", newLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationality, canton, readOnly]);

  const getRequiredSelectStyles = (fieldName: "nationality" | "visaType") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = typeof value !== "string" || value.trim() === "";

    return getRequiredFieldStyles({
      readOnly,
      isVisible: visibleFields.includes(fieldName),
      isEmpty,
    });
  };

  const getRequiredDatePickerStyles = (fieldName: "birthDate" | "visaFrom" | "visaTo") => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = !value;
    const isVisible = fieldName === "birthDate" || visibleFields.includes(fieldName);

    return getRequiredFieldStyles({
      readOnly,
      isVisible,
      isEmpty,
    });
  };

  return (
    <Box>
      <SectionTitle label={t("credit.personalInfo.sectionTitle")} isBankCredit />
      <GenderRadioGroup
        fieldName="gender"
        form={form}
        disabled={readOnly}
        genderOptions={options["gender"]}
      />

      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.firstName.label")}
          key={form.key("user.firstName")}
          {...form.getInputProps("user.firstName")}
          disabled={true}
          required={true}
        />
        <TextInput
          label={t("user.lastName.label")}
          key={form.key("user.lastName")}
          {...form.getInputProps("user.lastName")}
          disabled={true}
          required={true}
        />
        <DatePicker
          label={t("user.birthDate.label")}
          placeholder={t("user.birthDate.placeholder")}
          key={form.key("birthDate")}
          {...form.getInputProps("birthDate")}
          onChange={(value: Date | null) => {
            form.setFieldValue("birthDate", value || undefined);
            revalidateEmploymentDatesRef?.current?.(value || undefined, null);
          }}
          styles={getRequiredDatePickerStyles("birthDate")}
          minDate={moment().subtract(65, "years")}
          maxDate={moment().subtract(18, "years")}
          required={true}
          disabled={readOnly}
        />
      </SimpleGrid>
      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.phone.label")}
          key={form.key("user.phone")}
          {...form.getInputProps("user.phone")}
          rightSection={
            <CopyPhoneButton
              getValue={() => {
                const user = form.getValues().user;
                return user ? `${user.countryCode} ${user.phoneNumber}` : undefined;
              }}
            />
          }
          rightSectionPointerEvents="all"
          disabled={true}
          required={true}
        />
        <TextInput
          label={t("user.email.label")}
          key={form.key("user.email")}
          {...form.getInputProps("user.email")}
          disabled={true}
          required={true}
        />
      </SimpleGrid>

      <Box
        mt={"sm"}
        style={{
          alignItems: "flex-start",
          columnGap: "24px",
          display: "flex",
          flexWrap: "wrap",
          rowGap: "12px",
        }}
      >
        <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
          <InputLabel label={t("credit.workdayAvailability.label")} />
          <TimeRangeSlider
            min={options["workdayAvailability"]?.min ?? 0}
            max={options["workdayAvailability"]?.max ?? 23}
            maxWidth={availabilitySliderMaxWidth}
            valueFrom={form.getValues().workdayAvailabilityFrom}
            valueTo={form.getValues().workdayAvailabilityTo}
            disabled={readOnly}
            onChangeEnd={(valueFrom, valueTo) => {
              form.setValues({
                workdayAvailabilityFrom: valueFrom,
                workdayAvailabilityTo: valueTo,
              });
            }}
          />
          <Checkbox
            key={form.key("saturdayAvailability")}
            label={t("credit.saturdayAvailability.label")}
            {...form.getInputProps("saturdayAvailability", { type: "checkbox" })}
            customOnClick={() => {
              form.setFieldValue(
                "saturdayAvailabilityFrom",
                convertToTimeString(options["saturdayAvailability"]?.min || 0),
              );
              form.setFieldValue(
                "saturdayAvailabilityTo",
                convertToTimeString(options["saturdayAvailability"]?.max || 0),
              );
            }}
            disabled={readOnly}
            mt={"sm"}
          />
        </Box>

        {visibleFields.includes("saturdayAvailabilityFrom") &&
          visibleFields.includes("saturdayAvailabilityTo") && (
            <Box style={{ maxWidth: availabilitySliderMaxWidth, width: "100%" }}>
              <InputLabel label={t("credit.saturdayAvailability.label")} />
              <TimeRangeSlider
                min={options["saturdayAvailability"]?.min ?? 0}
                max={options["saturdayAvailability"]?.max ?? 23}
                maxWidth={availabilitySliderMaxWidth}
                valueFrom={form.getValues().saturdayAvailabilityFrom}
                valueTo={form.getValues().saturdayAvailabilityTo}
                disabled={readOnly}
                onChangeEnd={(valueFrom, valueTo) => {
                  form.setValues({
                    saturdayAvailabilityFrom: valueFrom,
                    saturdayAvailabilityTo: valueTo,
                  });
                }}
              />
            </Box>
          )}
      </Box>

      <SimpleGrid cols={4} mt="sm">
        <Select
          label={t("user.nationality.label")}
          data={nationalities}
          key={form.key("nationality")}
          {...form.getInputProps("nationality")}
          styles={getRequiredSelectStyles("nationality")}
          searchable
          clearable={true}
          required={true}
          disabled={readOnly}
        />
        {visibleFields.includes("visaType") && (
          <Select
            label={t("user.visa.label")}
            data={options["visaType"].map((el) => ({
              value: el,
              label: t(`user.visa.options.${el}`),
            }))}
            key={form.key("visaType")}
            {...form.getInputProps("visaType")}
            styles={getRequiredSelectStyles("visaType")}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("visaFrom") && (
          <DatePicker
            label={t("user.visa.from")}
            key={`${form.key("visaFrom")}-${visaInputKey}`}
            {...form.getInputProps("visaFrom")}
            onChange={(value: Date | null) => {
              const birthDate = form.getValues().birthDate;

              if (value && birthDate && moment(value).isBefore(birthDate, "day")) {
                form.setFieldError("visaFrom", t("user.visa.validation.beforeBirthDate"));
                setVisaInputKey((key) => key + 1);
                return;
              }

              form.setFieldValue("visaFrom", value ?? undefined);
              revalidateEmploymentDatesRef?.current?.(null, value ?? undefined);
            }}
            styles={getRequiredDatePickerStyles("visaFrom")}
            excludeDate={(date: Date) => {
              const birthDate = form.getValues().birthDate;

              return Boolean(birthDate && moment(date).isBefore(birthDate, "day"));
            }}
            maxDate={new Date()}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("visaTo") && (
          <DatePicker
            label={t("user.visa.to")}
            key={form.key("visaTo")}
            {...form.getInputProps("visaTo")}
            styles={getRequiredDatePickerStyles("visaTo")}
            minDate={new Date()}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt="sm">
        <Select
          label={t("bankCredit.creditLanguage.label")}
          data={creditLanguageOptions}
          key={form.key("creditLanguage")}
          {...form.getInputProps("creditLanguage")}
          clearable={true}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Box>
  );
};
